export type Section = {
  h2: string;
  paragraphs: string[];
  bullets?: string[];
  table?: { head: string[]; rows: string[][] };
  after?: string[];
  link?: { to: string; label: string };
  /** Optional second contextual link, rendered next to `link`. */
  link2?: { to: string; label: string };
};
export type Faq = { q: string; a: string; link?: { to: string; label: string } };

export type PageContent = {
  h1: string;
  title: string;
  description: string;
  intro: string;
  /** Optional "En bref" summary box rendered under the intro. */
  brief?: string;
  sections: Section[];
  proof?: { value: string; label: string }[];
  faq: Faq[];
};

export type LandingPage = {
  path: string;
  related: string[];
  /** Short anchor used in "À lire aussi" cards instead of the full H1. */
  shortName?: string;
  fr: PageContent;
  /** Omitted for French-only pages (English falls back to French). */
  en?: PageContent;
  schemaExtra?: Record<string, unknown>;
};

const LOCAL_FR = "Basée à Bordeaux, disponible partout en France et à l'international.";
const LOCAL_EN = "Based in Bordeaux, available across France and internationally.";

export const landingPages: Record<string, LandingPage> = {
  "fractional-cmo-france": {
    path: "/fractional-cmo-france",
    related: ["fractional-cmo", "directeur-marketing-externalise", "fractional-cmo-bordeaux"],
    fr: {
      h1: "Fractional CMO en France : une direction marketing à distance, partout en France",
      title: "Fractional CMO en France : mission à distance | CMO Partner",
      description: "Une Fractional CMO pilote votre marketing à distance, partout en France : stratégie, équipe et outils, 2 à 12 jours par mois, tarif sur devis. Basée à Bordeaux.",
      intro: "Vous recherchez une Fractional CMO en France pour piloter votre marketing sans recruter une direction à temps plein ? Basée à Bordeaux, j'accompagne les PME, scale-ups et ETI B2B et B2C partout en France. La mission associe stratégie, arbitrages et pilotage de votre équipe et de vos prestataires, avec un format de 2 à 12 jours par mois, sur devis.",
      sections: [
        {
          h2: "Une responsabilité de direction, pas un forfait de campagnes",
          paragraphs: [
            "Le terme Fractional CMO désigne une direction marketing senior à temps partagé. En France, on parle aussi de direction marketing externalisée. Je travaille avec la direction générale pour définir les priorités, arbitrer les budgets et coordonner la mise en œuvre avec les ressources déjà en place.",
            "L'offre ne remplace pas une agence de production ni une équipe opérationnelle à temps plein. Elle apporte un pilotage transversal : positionnement, acquisition, parcours client, CRM et mesure de la performance. Le périmètre retenu dépend de votre situation et se précise lors du cadrage.",
          ],
          link: { to: "/directeur-marketing-externalise", label: "Le rôle du directeur marketing à temps partagé en détail →" },
        },
        {
          h2: "Pour quelles entreprises en France ?",
          paragraphs: ["Le critère principal est votre besoin de leadership marketing, plutôt que votre localisation. L'accompagnement concerne les entreprises qui disposent d'une équipe ou de partenaires à piloter, mais n'ont pas nécessairement besoin d'une direction marketing à temps plein."],
          bullets: [
            "PME et ETI : clarifier la stratégie, les responsabilités et l'allocation du budget marketing.",
            "Entreprises B2B et SaaS : articuler marketing et ventes, structurer le parcours des prospects et le suivi du pipeline.",
            "Marques B2C et e-commerce : coordonner acquisition, conversion et fidélisation à partir des données disponibles.",
            "Scale-ups ou équipes en évolution : accompagner un responsable marketing, coordonner les prestataires ou préparer un relais interne.",
          ],
          after: ["Si votre besoin porte uniquement sur la réalisation d'une campagne, un prestataire opérationnel peut être plus adapté. Si la présence quotidienne d'une direction est indispensable, un recrutement ou une mission de transition mérite d'être étudié."],
          link: { to: "/fractional-cmo", label: "Comprendre les missions et les différences entre les formats →" },
        },
        {
          h2: "Comment travailler ensemble partout en France ?",
          paragraphs: [
            "La majorité des missions se déroule à distance, avec des points hebdomadaires et des outils collaboratifs. Le pilotage s'appuie sur les objectifs partagés, la feuille de route, les données disponibles et les décisions à prendre avec votre équipe. Il ne dépend pas d'une présence permanente dans vos locaux.",
            "J'interviens en présentiel à Bordeaux, en Gironde et en Nouvelle-Aquitaine. Ailleurs en France, des déplacements ponctuels peuvent être envisagés selon les besoins, notamment pour les ateliers de stratégie ou les comités de direction. Leur rythme et leurs modalités sont à convenir lors du cadrage : il ne s'agit pas d'une présence locale permanente dans chaque ville.",
            "L'accompagnement peut se dérouler en français et en anglais, notamment lorsque vos équipes ou vos marchés sont internationaux.",
          ],
          link: { to: "/fractional-cmo-bordeaux", label: "Voir les modalités d'intervention à Bordeaux →" },
        },
        {
          h2: "Du diagnostic au pilotage de la mission",
          paragraphs: [
            "Le diagnostic en 2 semaines examine vos objectifs, votre organisation, les actions engagées, les outils et les données disponibles. Il sert à identifier les sujets prioritaires et à cadrer la suite, pas à garantir un résultat commercial dans ce délai.",
            "Nous définissons ensuite la feuille de route, les responsabilités, le budget et les indicateurs de suivi. Selon le besoin, les livrables peuvent comprendre une priorisation marketing, une organisation du pilotage, un cadrage CRM ou un tableau de bord. Ils sont retenus ensemble, plutôt qu'imposés par un forfait standard.",
            "Pendant la mission, je coordonne les équipes et les partenaires, accompagne les arbitrages et revois les priorités à partir des données observées. La montée en compétences peut associer coaching, ateliers et accompagnement sur les outils utilisés par l'équipe.",
          ],
          link: { to: "/services/montee-en-competences-marketing", label: "Découvrir l'accompagnement de votre équipe →" },
        },
        {
          h2: "Rythme, durée et tarif : un format à cadrer",
          paragraphs: [
            "L'intervention représente 2 à 12 jours par mois. Le nombre de jours dépend du périmètre, de l'organisation de votre équipe et des responsabilités confiées. La durée de la mission, les points de suivi et les modalités d'évolution sont convenus ensemble.",
            "Les tarifs sont sur devis. Le premier échange permet de préciser le besoin avant de proposer un périmètre et un rythme ; aucun tarif journalier ni prix national standard n'est affiché.",
          ],
          link: { to: "/blog/fractional-cmo-definition-missions-tarifs", label: "Lire le guide des missions, formats et tarifs →" },
        },
        {
          h2: "Présenter votre besoin avant de démarrer",
          paragraphs: [
            "Réservez un appel de 30 minutes pour évoquer votre activité, vos objectifs, votre équipe actuelle et vos difficultés prioritaires. Précisez aussi où se trouvent les personnes impliquées et si certains ateliers nécessitent une présence sur place. Ces éléments permettent d'examiner si une direction à temps partagé correspond à votre situation.",
            "Si vous préférez écrire, le formulaire de contact permet de transmettre votre contexte. Vous pouvez également consulter le parcours de CMO Partner pour apprécier l'expérience au regard de votre besoin.",
          ],
          link: { to: "/contact", label: "Présenter votre projet par écrit →" },
        },
        {
          h2: "Choisir une partenaire adaptée à votre organisation",
          paragraphs: [
            "Au-delà du lieu d'intervention, vérifiez l'expérience en direction marketing, la connaissance de votre modèle B2B ou B2C et la capacité à travailler avec vos équipes et vos agences. Clarifiez les responsabilités, les livrables et les modalités de collaboration avant de vous engager.",
            "Le parcours présenté sur ce site comprend le pilotage d'équipes et de marchés en France et à l'international. Il permet d'étudier l'adéquation avec votre contexte, sans présumer de résultats identiques pour votre entreprise.",
          ],
          link: { to: "/a-propos", label: "Mon parcours →" },
        },
      ],
      faq: [],
    },
  },
  "fractional-cmo": {
    path: "/fractional-cmo",
    related: ["fractional-cmo-b2b-saas", "fractional-cmo-b2c-ecommerce", "fractional-cmo-bordeaux", "fractional-cmo-france"],
    fr: {
      h1: "Fractional CMO — Direction Marketing à Temps Partagé",
      title: "Fractional CMO en France | CMO Partner — CMOPartner",
      description:
        "Fractional CMO expérimentée, 16 ans de leadership marketing international. Ex-Decathlon, Lavazza. Démarrage en 2 semaines.",
      intro:
        "Une direction marketing senior, 2 à 12 jours par mois, intégrée à votre comité de direction. Vous obtenez la stratégie, les arbitrages et l'exécution d'un CMO expérimenté sans le coût d'un recrutement à temps plein. " +
        LOCAL_FR,
      sections: [
        {
          h2: "Qu'est-ce qu'un Fractional CMO",
          paragraphs: [
            "Un Fractional CMO est un directeur marketing senior qui intervient à temps partagé dans une entreprise, généralement 2 à 12 jours par mois. Il porte la stratégie marketing, pilote l'équipe, définit les priorités d'acquisition et rend des comptes sur les résultats — exactement comme un CMO salarié, sur une fraction du temps.",
            "À la différence d'un consultant, le Fractional CMO s'intègre dans l'organisation : il siège au comité de direction, manage les ressources internes et externes, et reste responsable de la performance dans la durée.",
          ],
        },
        {
          h2: "Pourquoi choisir un Fractional CMO",
          paragraphs: [
            "Parce que la plupart des PME, scale-ups et ETI ont besoin d'un cap stratégique, pas d'une paire de bras supplémentaire.",
          ],
          bullets: [
            "Expertise de niveau COMEX immédiatement opérationnelle, sans période de recrutement de 6 mois.",
            "Coût divisé par 3 à 5 par rapport à un CMO en CDI (80 à 150 k€ par an en coût total employeur).",
            "Engagement flexible : on augmente ou on réduit le rythme selon la phase de croissance.",
            "Transfert de compétences : l'équipe interne est formée pour prendre le relais.",
          ],
        },
        {
          h2: "Fractional CMO vs CMO full-time",
          paragraphs: [
            "Un CMO full-time se justifie quand le marketing représente une organisation de plus de 8 à 10 personnes avec un budget média conséquent. En dessous, vous payez un salaire complet pour un besoin réel de 30 %.",
            "Le Fractional CMO couvre la partie stratégique et structurante — positionnement, priorisation, architecture CRM, pilotage de la performance — et laisse l'exécution quotidienne à l'équipe ou aux prestataires déjà en place.",
          ],
        },
        {
          h2: "Missions concrètes",
          paragraphs: ["Quatre types d'intervention, toujours orientés système plutôt que livrables ponctuels."],
          bullets: [
            "Audit des actifs marketing existants et diagnostic de conversion en 2 semaines.",
            "Architecture de croissance : HubSpot, scoring, nurture, automation.",
            "Pilotage de la performance, pricing et arbitrages budgétaires avec le CEO.",
            "Automatisation et agents IA pour récupérer 30 à 50 % du temps de l'équipe marketing.",
          ],
        },
      ],
      faq: [
        {
          q: "Qu'est-ce qu'un Fractional CMO ?",
          a: "Un Fractional CMO est un directeur marketing senior qui intervient à temps partagé dans une entreprise, généralement 2 à 12 jours par mois. Il définit la stratégie marketing, pilote l'équipe et les prestataires, structure l'acquisition et est responsable des résultats, sans le coût ni l'engagement d'un recrutement à temps plein.",
        },
        {
          q: "Combien coûte un Fractional CMO ?",
          a: "Un CMO en CDI représente 80 à 150 k€ par an en coût total employeur. Avec un Fractional CMO, vous ne payez que les jours réellement nécessaires (de 2 à 12 par mois), sans charges sociales, avec un engagement mensuel ajustable selon la phase de croissance de l'entreprise.",
        },
        {
          q: "Quelle différence entre Fractional CMO et agence ?",
          a: "Une agence exécute des campagnes selon un périmètre défini. Un Fractional CMO décide de la stratégie, arbitre les budgets, pilote les agences et l'équipe interne, et siège au comité de direction. Il est côté client, responsable du résultat business, pas de la production.",
        },
        {
          q: "Quand faire appel à un Fractional CMO ?",
          a: "Quand vous avez des actifs marketing qui ne convertissent pas assez, une équipe qui exécute sans cap stratégique, un départ ou une transition de directeur marketing, une levée de fonds à préparer, ou un besoin de leadership senior sans budget pour un CMO à temps plein.",
        },
        {
          q: "Comment choisir son Fractional CMO ?",
          a: "Vérifiez l'expérience réelle en direction marketing (et non uniquement en conseil), la présence en comité de direction, des résultats chiffrés vérifiables, la connaissance de votre modèle (B2B SaaS ou B2C e-commerce) et la capacité à former vos équipes pour que le système continue de tourner après la mission.",
        },
      ],
    },
    en: {
      h1: "Fractional CMO France — International Marketing Leadership",
      title: "Fractional CMO France | CMO Partner — CMOPartner",
      description:
        "Experienced Fractional CMO with 16 years of international marketing leadership. Ex-Decathlon, Lavazza. Start within 2 weeks.",
      intro:
        "Senior marketing leadership, 2 to 12 days per month, embedded in your executive team. You get the strategy, the trade-offs and the execution discipline of an experienced CMO without a full-time hire. " +
        LOCAL_EN,
      sections: [
        {
          h2: "What is a Fractional CMO",
          paragraphs: [
            "A Fractional CMO is a senior marketing director who works part-time inside a company, usually 2 to 12 days per month. They own marketing strategy, lead the team, set acquisition priorities and are accountable for results — exactly like a full-time CMO, on a fraction of the time.",
            "Unlike a consultant, they are embedded in the organisation: they sit on the executive committee, manage internal and external resources, and stay accountable over time.",
          ],
        },
        {
          h2: "Why choose a Fractional CMO",
          paragraphs: ["Most SMEs and scale-ups need strategic direction, not an extra pair of hands."],
          bullets: [
            "Executive-level expertise available immediately, with no six-month hiring process.",
            "You only pay for the days you need (2 to 12 per month), whereas a full-time CMO costs €80–150k per year in total employer cost.",
            "Flexible commitment that scales up or down with your growth stage.",
            "Knowledge transfer: your internal team is trained to take over.",
          ],
        },
        {
          h2: "Fractional CMO vs full-time CMO",
          paragraphs: [
            "A full-time CMO makes sense once marketing is a team of 8 to 10 people with a significant media budget. Below that, you pay a full salary for a 30% need.",
            "A Fractional CMO covers the strategic and structural work — positioning, prioritisation, CRM architecture, performance management — and leaves day-to-day execution to your team or existing partners.",
          ],
        },
        {
          h2: "Concrete engagements",
          paragraphs: ["Four types of engagement, always focused on systems rather than one-off deliverables."],
          bullets: [
            "Audit of existing marketing assets and conversion diagnosis within 2 weeks.",
            "Growth architecture: HubSpot, scoring, nurture, automation.",
            "Performance management, pricing and budget trade-offs with the CEO.",
            "Automation and AI agents to recover 30 to 50% of your marketing team's time.",
          ],
        },
      ],
      faq: [
        {
          q: "What is a Fractional CMO?",
          a: "A Fractional CMO is a senior marketing director working part-time for a company, typically 2 to 12 days per month. They set marketing strategy, lead the team and agencies, structure acquisition and own the results, without the cost or commitment of a full-time hire.",
        },
        {
          q: "How much does a Fractional CMO cost?",
          a: "A full-time CMO costs €80–150k per year in total employer cost. With a Fractional CMO, you only pay for the days you need (2 to 12 per month), with a monthly commitment that can be adjusted as the company grows.",
        },
        {
          q: "What is the difference between a Fractional CMO and an agency?",
          a: "An agency executes campaigns within a defined scope. A part-time CMO decides strategy, arbitrates budgets, manages agencies and the internal team, and sits on the leadership team. They are on the client side, accountable for business results rather than production.",
        },
        {
          q: "When should you hire a Fractional CMO?",
          a: "When your marketing assets underperform, your team executes without strategic direction, you are between marketing directors, you are preparing a fundraise, or you need senior leadership without the budget for a full-time CMO.",
        },
        {
          q: "How do you choose a Fractional CMO?",
          a: "Look for real in-house leadership experience rather than consulting only, executive committee exposure, verifiable measurable results, knowledge of your model (B2B SaaS or B2C e-commerce), and the ability to train your team so the system keeps running after the engagement.",
        },
      ],
    },
  },

  "fractional-cmo-b2b-saas": {
    path: "/fractional-cmo-b2b-saas",
    related: ["fractional-cmo", "fractional-cmo-b2c-ecommerce", "crm-lifecycle", "fractional-cmo-france"],
    fr: {
      h1: "Fractional CMO B2B SaaS — Acquisition, CRM & Growth Architecture",
      title: "Fractional CMO B2B SaaS | HubSpot, pipeline & lifecycle",
      description:
        "Fractional CMO spécialisée B2B SaaS. HubSpot, Salesforce, lifecycle marketing, pipeline generation. Résultats mesurables.",
      intro:
        "Pour les éditeurs SaaS et les entreprises B2B avec une équipe commerciale : je structure l'acquisition, l'architecture CRM et la mécanique de pipeline qui transforme le trafic en revenus prévisibles. " +
        LOCAL_FR,
      sections: [
        {
          h2: "Acquisition et génération de pipeline",
          paragraphs: [
            "Le problème n'est presque jamais le volume de leads : c'est la qualification, le routage et le suivi. Je remets à plat le parcours depuis la première visite jusqu'à l'opportunité signée, en alignant marketing et sales sur une définition commune du lead qualifié.",
          ],
          bullets: [
            "Segmentation ICP et priorisation des canaux réellement rentables.",
            "Scoring comportemental et routage automatique vers les commerciaux.",
            "Séquences de nurturing par étape du cycle de vie.",
            "Reporting pipeline unifié marketing / sales.",
          ],
        },
        {
          h2: "Architecture HubSpot et lifecycle",
          paragraphs: [
            "HubSpot ou Salesforce mal configurés travaillent contre l'équipe commerciale. Je reconstruis les propriétés, les étapes de cycle de vie, les workflows et l'attribution pour que chaque action marketing soit traçable jusqu'au chiffre d'affaires.",
          ],
        },
        {
          h2: "Résultats obtenus",
          paragraphs: [
            "smsmode : +50 % de leads inbound, +25 % de ROI marketing, plus de 10 M€ de MRR supportés avec une équipe de plus de 10 personnes. Lumivero : +30 % de croissance du chiffre d'affaires et 1 M€ de nouveaux marchés ouverts.",
          ],
        },
      ],
      proof: [
        { value: "+50%", label: "Leads inbound · smsmode" },
        { value: "+25%", label: "ROI marketing · smsmode" },
        { value: "10M€+", label: "MRR supporté · smsmode" },
        { value: "+30%", label: "Croissance CA · Lumivero" },
      ],
      faq: [
        {
          q: "Qu'apporte un Fractional CMO à un éditeur SaaS ?",
          a: "Il structure l'acquisition et le lifecycle : définition de l'ICP, scoring, nurturing, alignement marketing-sales et reporting pipeline. L'objectif est un flux de leads qualifiés prévisible et mesurable, pas une hausse de trafic sans conversion.",
        },
        {
          q: "Faut-il déjà avoir HubSpot pour travailler ensemble ?",
          a: "Non. Je déploie HubSpot depuis zéro ou je restructure une instance existante, et je travaille également sur Salesforce ou un CRM équivalent déjà en place.",
        },
        {
          q: "En combien de temps voit-on des résultats en B2B SaaS ?",
          a: "Les premiers effets mesurables apparaissent généralement entre le premier et le troisième mois : une première boucle segment / séquence / scoring est lancée dès le premier mois, puis industrialisée en mois 2 et 3.",
        },
        {
          q: "Travaillez-vous avec l'équipe commerciale ?",
          a: "Oui. En B2B SaaS, le marketing ne produit de résultat que s'il est aligné avec le sales : définitions communes, SLA de traitement des leads, feedback loop sur la qualité et reporting partagé.",
        },
      ],
    },
    en: {
      h1: "Fractional CMO B2B SaaS — Acquisition, CRM & Growth Architecture",
      title: "Fractional CMO for B2B SaaS | HubSpot, pipeline & lifecycle",
      description:
        "Fractional CMO specialised in B2B SaaS. HubSpot, Salesforce, lifecycle marketing, pipeline generation. Measurable results.",
      intro:
        "For SaaS vendors and B2B companies with a sales team: I structure acquisition, CRM architecture and the pipeline engine that turns traffic into predictable revenue. " +
        LOCAL_EN,
      sections: [
        {
          h2: "Acquisition and pipeline generation",
          paragraphs: [
            "The problem is rarely lead volume: it is qualification, routing and follow-up. I rebuild the journey from first visit to closed opportunity and align marketing and sales on a shared definition of a qualified lead.",
          ],
          bullets: [
            "ICP segmentation and prioritisation of genuinely profitable channels.",
            "Behavioural scoring and automated routing to sales reps.",
            "Nurture sequences per lifecycle stage.",
            "Unified marketing and sales pipeline reporting.",
          ],
        },
        {
          h2: "HubSpot architecture and lifecycle",
          paragraphs: [
            "A badly configured HubSpot or Salesforce works against the sales team. I rebuild properties, lifecycle stages, workflows and attribution so every marketing action is traceable to revenue.",
          ],
        },
        {
          h2: "Results delivered",
          paragraphs: [
            "smsmode: +50% inbound leads, +25% marketing ROI, €10M+ MRR supported with a team of more than 10 people. Lumivero: +30% revenue growth and €1M of new markets opened.",
          ],
        },
      ],
      proof: [
        { value: "+50%", label: "Inbound leads · smsmode" },
        { value: "+25%", label: "Marketing ROI · smsmode" },
        { value: "€10M+", label: "MRR supported · smsmode" },
        { value: "+30%", label: "Revenue growth · Lumivero" },
      ],
      faq: [
        {
          q: "What does a Fractional CMO bring to a SaaS company?",
          a: "They structure acquisition and lifecycle: ICP definition, scoring, nurturing, marketing-sales alignment and pipeline reporting. The goal is a predictable, measurable flow of qualified leads rather than traffic that never converts.",
        },
        {
          q: "Do we need HubSpot already?",
          a: "No. I deploy HubSpot from scratch or restructure an existing instance, and I also work with Salesforce or an equivalent CRM already in place.",
        },
        {
          q: "How fast do B2B SaaS results appear?",
          a: "Measurable effects usually appear between month one and month three: a first segment / sequence / scoring loop goes live in month one, then gets industrialised in months two and three.",
        },
        {
          q: "Do you work with the sales team?",
          a: "Yes. In B2B SaaS, marketing only produces results when aligned with sales: shared definitions, lead response SLAs, quality feedback loops and shared reporting.",
        },
      ],
    },
  },

  "fractional-cmo-b2c-ecommerce": {
    path: "/fractional-cmo-b2c-ecommerce",
    related: ["fractional-cmo", "fractional-cmo-b2b-saas", "growth-architecture", "fractional-cmo-france"],
    fr: {
      h1: "Fractional CMO B2C & E-commerce — Stratégie, Pricing & Influence",
      title: "Fractional CMO B2C & E-commerce | Stratégie, pricing, influence",
      description:
        "Fractional CMO B2C expérimentée. Ex-Decathlon 10 ans, Lavazza. E-commerce, pricing, stratégie omnicanale internationale.",
      intro:
        "Pour les marques consumer, scale-ups et sites e-commerce : stratégie de marque, pricing, influence et pilotage de la performance, directement avec le CEO. " +
        LOCAL_FR,
      sections: [
        {
          h2: "Stratégie de marque et pricing",
          paragraphs: [
            "10 ans chez Decathlon à l'international et un passage chez Lavazza m'ont appris une chose : la croissance B2C se joue autant sur le pricing et la proposition de valeur que sur le budget média. Je remets la marque et l'architecture de prix au centre avant d'augmenter les investissements d'acquisition.",
          ],
          bullets: [
            "Positionnement, architecture de gamme et politique tarifaire.",
            "Stratégie d'influence et de contenu adossée aux objectifs commerciaux.",
            "Pilotage omnicanal : retail, marketplace, e-commerce propriétaire.",
            "Rentabilité par canal, coût d'acquisition et valeur client.",
          ],
        },
        {
          h2: "E-commerce et performance",
          paragraphs: [
            "Chez MaxiCoffee, j'ai accompagné un replatforming Salesforce Commerce Cloud tout en maintenant la performance d'acquisition. Refonte, migration, internationalisation : ces chantiers échouent quand personne ne tient la ligne business.",
          ],
        },
        {
          h2: "Références",
          paragraphs: [
            "Decathlon (10 ans, marketing international), Lavazza, MaxiCoffee (replatforming SFCC), Mikado. Membre du comité de direction dans chacun de ces rôles.",
          ],
        },
      ],
      faq: [
        {
          q: "Quelles marques B2C accompagnez-vous ?",
          a: "Des marques consumer, des scale-ups et des sites e-commerce, généralement entre 10 et 200 salariés, qui ont besoin d'une direction marketing senior sans recruter un CMO à temps plein.",
        },
        {
          q: "Intervenez-vous sur le pricing ?",
          a: "Oui. Le pricing est un levier de marge immédiat et souvent sous-exploité : architecture de gamme, positionnement prix, promotions et politique de remise font partie du périmètre.",
        },
        {
          q: "Gérez-vous les campagnes d'influence ?",
          a: "Je définis la stratégie d'influence, les critères de sélection et les indicateurs de performance, et je pilote les agences ou l'équipe interne qui exécutent les campagnes.",
        },
        {
          q: "Pouvez-vous accompagner une refonte e-commerce ?",
          a: "Oui, côté business : cadrage des priorités, arbitrages fonctionnels, maintien de la performance d'acquisition pendant la migration et pilotage des prestataires techniques.",
        },
      ],
    },
    en: {
      h1: "Fractional CMO B2C & E-commerce — Strategy, Pricing & Influence",
      title: "Fractional CMO B2C & E-commerce | Strategy, pricing, influence",
      description:
        "Experienced B2C Fractional CMO. Ex-Decathlon (10 years), Lavazza. E-commerce, pricing and international omnichannel strategy.",
      intro:
        "For consumer brands, scale-ups and e-commerce businesses: brand strategy, pricing, influence and performance management, working directly with the CEO. " +
        LOCAL_EN,
      sections: [
        {
          h2: "Brand strategy and pricing",
          paragraphs: [
            "Ten years at Decathlon internationally and a stint at Lavazza taught me one thing: B2C growth depends as much on pricing and value proposition as on media budget. I put brand and price architecture back at the centre before increasing acquisition spend.",
          ],
          bullets: [
            "Positioning, range architecture and pricing policy.",
            "Influence and content strategy tied to commercial objectives.",
            "Omnichannel steering: retail, marketplaces, owned e-commerce.",
            "Channel profitability, acquisition cost and customer value.",
          ],
        },
        {
          h2: "E-commerce and performance",
          paragraphs: [
            "At MaxiCoffee I supported a Salesforce Commerce Cloud replatforming while maintaining acquisition performance. Redesigns, migrations and internationalisation fail when nobody holds the business line.",
          ],
        },
        {
          h2: "Track record",
          paragraphs: [
            "Decathlon (10 years, international marketing), Lavazza, MaxiCoffee (SFCC replatforming), Mikado. Executive committee member in each of these roles.",
          ],
        },
      ],
      faq: [
        {
          q: "Which B2C brands do you work with?",
          a: "Consumer brands, scale-ups and e-commerce businesses, usually between 10 and 200 employees, that need senior marketing leadership without hiring a full-time CMO.",
        },
        {
          q: "Do you work on pricing?",
          a: "Yes. Pricing is an immediate and often underused margin lever: range architecture, price positioning, promotions and discount policy are all in scope.",
        },
        {
          q: "Do you manage influencer campaigns?",
          a: "I define the influence strategy, selection criteria and performance metrics, and manage the agencies or internal team executing the campaigns.",
        },
        {
          q: "Can you support an e-commerce replatforming?",
          a: "Yes, on the business side: priority framing, functional trade-offs, protecting acquisition performance during migration and steering technical partners.",
        },
      ],
    },
  },

  "fractional-cmo-bordeaux": {
    path: "/fractional-cmo-bordeaux",
    related: ["fractional-cmo", "directeur-marketing-externalise", "fractional-cmo-b2b-saas"],
    fr: {
      h1: "Fractional CMO à Bordeaux — CMO Partner",
      title: "Fractional CMO à Bordeaux | Direction marketing à temps partagé",
      description:
        "Fractional CMO basée à Bordeaux, disponible en France et à l'international. Missions remote ou présentiel.",
      intro:
        "Basée à Bordeaux, j'accompagne les entreprises de Gironde et de Nouvelle-Aquitaine en présentiel, et le reste de la France en remote. Direction marketing senior, 2 à 12 jours par mois.",
      sections: [
        {
          h2: "Une direction marketing senior ancrée à Bordeaux",
          paragraphs: [
            "Bordeaux, Mérignac, Pessac, Bègles, Arcachon : l'écosystème néo-aquitain compte de nombreuses PME, ETI et scale-ups qui ont dépassé le stade du marketing improvisé sans pouvoir financer un CMO à temps plein. C'est exactement le besoin que couvre un Fractional CMO.",
            "En présentiel, je participe aux comités de direction, aux points d'équipe et aux ateliers de cadrage. Le reste du travail se fait à distance, avec le même niveau d'engagement.",
          ],
        },
        {
          h2: "Missions en Nouvelle-Aquitaine et partout en France",
          paragraphs: [
            "Les missions couvrent la stratégie marketing, l'architecture CRM, l'acquisition, le pricing et l'automatisation. Le modèle fonctionne aussi bien pour une scale-up bordelaise que pour une entreprise française ou européenne travaillant en remote.",
          ],
          bullets: [
            "Présentiel régulier à Bordeaux et en Gironde.",
            "Missions remote en France entière et à l'international.",
            "Bilingue français / anglais, expérience internationale.",
            "Démarrage sous 2 semaines.",
          ],
        },
      ],
      faq: [
        {
          q: "Intervenez-vous en présentiel à Bordeaux ?",
          a: "Oui. Je suis basée à Bordeaux et j'interviens en présentiel à Bordeaux, Mérignac, Pessac et plus largement en Gironde et en Nouvelle-Aquitaine, avec un rythme défini avec le client.",
        },
        {
          q: "Travaillez-vous avec des entreprises hors de Bordeaux ?",
          a: "Oui. La majorité des missions se déroulent en remote, en France entière comme à l'international, avec des déplacements ponctuels pour les comités de direction et les ateliers clés.",
        },
        {
          q: "Quels types d'entreprises bordelaises accompagnez-vous ?",
          a: "Des PME, ETI, éditeurs SaaS et marques consumer, typiquement entre 10 et 200 salariés, en phase de structuration ou de croissance de leur marketing.",
        },
        {
          q: "Quel est le délai pour démarrer une mission ?",
          a: "En général deux semaines, le temps de cadrer le périmètre, le rythme d'intervention et les objectifs des 90 premiers jours.",
        },
      ],
    },
    en: {
      h1: "Fractional CMO in Bordeaux — CMO Partner",
      title: "Fractional CMO in Bordeaux | Part-time marketing leadership",
      description:
        "Fractional CMO based in Bordeaux, available across France and internationally. Remote or on-site engagements.",
      intro:
        "Based in Bordeaux, I work on-site with companies across Gironde and Nouvelle-Aquitaine, and remotely everywhere else. Senior marketing leadership, 2 to 12 days per month.",
      sections: [
        {
          h2: "Senior marketing leadership rooted in Bordeaux",
          paragraphs: [
            "Bordeaux, Mérignac, Pessac, Bègles, Arcachon: the Nouvelle-Aquitaine ecosystem is full of SMEs and scale-ups that have outgrown improvised marketing but cannot yet fund a full-time CMO. That is exactly what a Fractional CMO solves.",
            "On site, I join leadership meetings, team reviews and framing workshops. The rest of the work happens remotely, with the same level of commitment.",
          ],
        },
        {
          h2: "Engagements across France and beyond",
          paragraphs: [
            "Engagements cover marketing strategy, CRM architecture, acquisition, pricing and automation. The model works equally well for a Bordeaux scale-up and for a French or European company working remotely.",
          ],
          bullets: [
            "Regular on-site presence in Bordeaux and Gironde.",
            "Remote engagements across France and internationally.",
            "Bilingual French / English with international experience.",
            "Start within 2 weeks.",
          ],
        },
      ],
      faq: [
        {
          q: "Do you work on site in Bordeaux?",
          a: "Yes. I am based in Bordeaux and work on site in Bordeaux, Mérignac, Pessac and more broadly across Gironde and Nouvelle-Aquitaine, at a rhythm agreed with the client.",
        },
        {
          q: "Do you work with companies outside Bordeaux?",
          a: "Yes. Most engagements run remotely, across France and internationally, with occasional travel for leadership meetings and key workshops.",
        },
        {
          q: "Which Bordeaux companies do you support?",
          a: "SMEs, mid-caps, SaaS vendors and consumer brands, typically 10 to 200 employees, structuring or scaling their marketing.",
        },
        {
          q: "How quickly can an engagement start?",
          a: "Usually two weeks, the time needed to frame the scope, the rhythm and the objectives for the first 90 days.",
        },
      ],
    },
  },

  "directeur-marketing-externalise": {
    path: "/directeur-marketing-externalise",
    related: ["fractional-cmo", "fractional-cmo-bordeaux", "crm-lifecycle", "fractional-cmo-france"],
    shortName: "Directeur marketing externalisé",
    fr: {
      h1: "Directeur marketing à temps partagé ou externalisé pour PME et ETI",
      title: "Directeur marketing à temps partagé ou externalisé | CMO Partner",
      description:
        "Un directeur marketing à temps partagé pilote stratégie, équipe et CRM de votre PME ou ETI, 2 à 12 jours par mois, sans recrutement CDI. Tarif sur devis.",
      intro:
        "Vous cherchez un directeur marketing externalisé pour structurer votre marketing sans recruter à temps plein ? En tant que directrice marketing externalisée, j'accompagne les PME et ETI B2B et B2C sur la stratégie, les arbitrages budgétaires, le pilotage des équipes et le suivi de la performance. Le format s'adapte à votre besoin : 2 à 12 jours par mois, sur devis. Basée à Bordeaux, disponible partout en France.",
      brief:
        "Un directeur marketing à temps partagé, aussi appelé directeur marketing externalisé ou Fractional CMO, est un dirigeant marketing senior qui pilote stratégie, équipe et outils d'une entreprise quelques jours par mois, sans recrutement en CDI. CMO Partner, basé à Bordeaux, intervient auprès de PME et ETI de 2 à 12 jours par mois selon le format, partout en France. Les premières actions démarrent dans le premier mois. Le tarif est établi sur devis après un premier échange.",
      sections: [
        {
          h2: "Directeur marketing à temps partagé, externalisé, Fractional CMO : même rôle, trois noms",
          paragraphs: [
            "Ces trois expressions désignent le même rôle : une direction marketing senior qui intervient quelques jours par mois, sans être salariée de l'entreprise. « Directeur marketing à temps partagé » décrit le rythme d'intervention, « directeur marketing externalisé » le fait que la fonction est confiée à un prestataire extérieur, et « Fractional CMO » est le terme anglo-saxon, courant dans le SaaS et les scale-ups.",
            "Dans les trois cas, la personne prend la responsabilité de la fonction marketing : stratégie, budget, pilotage de l'équipe et des prestataires, reporting à la direction générale. Ce qui la distingue d'un consultant ou d'une agence, c'est cette responsabilité de direction, pas le vocabulaire employé.",
            "Sur ce site, les trois termes renvoient à la même offre, détaillée sur la page Fractional CMO.",
          ],
          table: {
            head: ["Terme", "Ce qu'il met en avant", "Quand on l'emploie"],
            rows: [
              ["Directeur marketing à temps partagé", "Le rythme : quelques jours par mois", "Usage courant en France, PME et ETI"],
              ["Directeur marketing externalisé", "La fonction confiée à un prestataire extérieur", "Comparaison avec un recrutement interne"],
              ["Fractional CMO", "Le partage du temps entre plusieurs entreprises", "SaaS, scale-ups et contextes internationaux"],
            ],
          },
          link: { to: "/fractional-cmo", label: "Fractional CMO : définition, missions et formats →" },
        },
        {
          h2: "Pour quelles entreprises et quelles situations ?",
          paragraphs: [
            "Cette offre s'adresse aux PME et ETI qui ont besoin d'une direction marketing senior, mais pas nécessairement d'un poste à temps plein. Elle concerne aussi bien les activités B2B, dont le SaaS, que les marques B2C et l'e-commerce.",
            "Le point de départ peut être une équipe opérationnelle à accompagner, des prestataires à coordonner ou une direction générale qui souhaite relier les actions marketing aux objectifs commerciaux.",
          ],
          bullets: [
            "Vos campagnes, contenus et outils existent, mais les priorités et les budgets restent à arbitrer.",
            "Vous souhaitez mieux articuler marketing et ventes, avec des indicateurs partagés.",
            "Votre CRM et vos données clients doivent être structurés pour suivre les prospects, la conversion ou la fidélisation.",
            "Vous préparez la structuration de l'équipe ou le recrutement d'une direction marketing interne.",
          ],
        },
        {
          h2: "Ce que couvre la mission et les livrables à cadrer",
          paragraphs: [
            "Je définis la stratégie et pilote sa mise en œuvre avec votre équipe et vos partenaires. Le périmètre est convenu avec la direction générale : il ne s'agit pas d'un forfait de production de campagnes ou de contenus.",
            "Selon vos priorités, la mission peut porter sur les éléments suivants. Les livrables retenus, leurs responsables et les modalités de suivi sont précisés lors du cadrage.",
          ],
          table: {
            head: ["Priorité", "Travail mené", "Livrable à cadrer"],
            rows: [
              ["Stratégie et budget", "Relier positionnement, acquisition et fidélisation aux objectifs commerciaux ; arbitrer les actions", "Feuille de route marketing priorisée et budget associé"],
              ["Équipe et prestataires", "Clarifier les responsabilités, coordonner les agences et accompagner le responsable marketing", "Répartition des rôles et organisation du pilotage"],
              ["CRM et donnée client", "Structurer les étapes du parcours client, les segments et les besoins de suivi", "Cadrage du CRM et des scénarios de cycle de vie"],
              ["Mesure et reporting", "Choisir les indicateurs utiles et organiser leur lecture avec la direction", "Tableau de bord et points d'arbitrage"],
            ],
          },
          link: { to: "/services/crm-lifecycle", label: "Découvrir l'accompagnement CRM et cycle de vie client →" },
        },
        {
          h2: "Adapter le pilotage à votre modèle B2B ou B2C",
          paragraphs: [
            "En B2B et SaaS, le travail porte notamment sur l'articulation marketing–ventes, le parcours des prospects et le suivi du pipeline. Pour une marque B2C ou un e-commerce, les priorités peuvent être l'acquisition, la conversion et la fidélisation. Le choix des actions dépend de votre modèle, de vos données disponibles et des ressources de l'équipe.",
          ],
          link: { to: "/fractional-cmo-b2b-saas", label: "Direction marketing pour les entreprises B2B et SaaS →" },
        },
        {
          h2: "Comment démarre une mission avec un directeur marketing à temps partagé ?",
          paragraphs: [
            "Le diagnostic examine vos objectifs, les actions en cours, les outils, les données disponibles et l'organisation de l'équipe. Il sert à distinguer ce qui mérite d'être conservé, ce qui freine le parcours client et les décisions à prendre en priorité. C'est aussi à cette étape que se câble l'architecture de croissance : CRM, parcours client et mesure.",
            "À partir de cet état des lieux, nous cadrons la feuille de route, les responsabilités, le budget et les indicateurs de suivi. Le pilotage consiste ensuite à coordonner la mise en œuvre et à revoir les priorités à partir des données observées, sans promettre un résultat commercial à une date donnée.",
          ],
          link: { to: "/fractional-cmo-b2c-ecommerce", label: "Direction marketing pour les marques B2C et l'e-commerce →" },
          link2: { to: "/services/growth-architecture", label: "L'architecture de croissance : CRM, parcours client et mesure →" },
        },
        {
          h2: "Directeur marketing à temps partagé ou agence : que choisir ?",
          paragraphs: [
            "Le choix dépend de la responsabilité que vous souhaitez confier, pas seulement du statut de l'intervenant. Une direction à temps partagé peut piloter une agence ; elle ne remplace pas une équipe de production ni une présence interne à temps plein.",
          ],
          table: {
            head: ["Format", "Rôle", "Quand l'envisager"],
            rows: [
              ["Direction externalisée / Fractional CMO", "Stratégie, arbitrages et pilotage de l'équipe et des partenaires", "Vous avez besoin d'une responsabilité marketing senior à temps partagé"],
              ["Agence ou freelance opérationnel", "Production et exécution dans un périmètre défini", "Votre stratégie est cadrée et vous avez besoin de réaliser des actions"],
              ["Recrutement en CDI", "Direction marketing intégrée à temps plein", "Le volume et la continuité du besoin justifient un poste interne"],
              ["Management de transition", "Prise de responsabilité temporaire, notamment lors d'un remplacement", "Votre situation exige une présence dédiée ; la disponibilité doit être vérifiée"],
            ],
          },
          link: { to: "/blog/cmo-externalise-vs-agence-vs-recrutement", label: "Lire le comparatif : CMO externalisé, agence ou CDI →" },
        },
        {
          h2: "Direction de la communication externalisée",
          paragraphs: [
            "Pour les PME qui n'ont pas de direction de la communication, je peux aussi porter ce rôle, en cohérence avec la stratégie marketing. Son inclusion dans la mission est à préciser selon vos besoins.",
          ],
          bullets: [
            "Positionnement et image de marque.",
            "Plan de communication.",
            "Coordination des agences RP et contenu.",
            "Cohérence entre communication et marketing.",
          ],
        },
        {
          h2: "Accompagner votre équipe marketing",
          paragraphs: [
            "La mission peut associer pilotage et montée en compétences : coaching du responsable marketing, ateliers de travail et accompagnement sur les outils CRM et IA. Le contenu est adapté aux sujets traités et au niveau de l'équipe.",
          ],
          link: { to: "/services/montee-en-competences-marketing", label: "Découvrir la montée en compétences marketing →" },
        },
        {
          h2: "Format, tarif et premier échange",
          paragraphs: [
            "Les formats vont de 2 à 12 jours par mois. Le tarif est sur devis, selon le périmètre et le nombre de jours retenus. La durée, le rythme d'intervention et les modalités d'évolution de la mission sont à convenir ensemble.",
            "Réservez un appel de 30 minutes pour parler de votre situation : objectifs commerciaux, organisation actuelle, outils et difficultés prioritaires. Nous pourrons examiner si une direction à temps partagé correspond à votre besoin et quel périmètre approfondir. Si vous préférez écrire, le formulaire de contact permet de transmettre ces éléments.",
          ],
          link: { to: "/contact", label: "Présenter votre besoin par écrit →" },
          link2: { to: "/fractional-cmo-bordeaux", label: "Intervention en présentiel à Bordeaux et en Gironde →" },
        },
      ],
      faq: [
        {
          q: "Qu'est-ce qu'un directeur marketing externalisé ?",
          a: "C'est un directeur marketing expérimenté qui prend la responsabilité de la fonction marketing d'une entreprise à temps partagé, généralement 2 à 12 jours par mois, sans être salarié de l'entreprise. Il définit la stratégie, pilote l'équipe et les prestataires et rend compte des résultats à la direction générale.",
        },
        {
          q: "Quelle différence avec un responsable marketing freelance ?",
          a: "Un responsable marketing freelance intervient le plus souvent sur l'exécution : campagnes, contenus, outils. La direction marketing externalisée se situe au niveau de la direction : stratégie, budget, priorisation, management et reporting au comité de direction.",
        },
        {
          q: "Combien coûte un directeur marketing externalisé ?",
          a: "Le tarif est sur devis, selon le périmètre de la mission et le nombre de jours d'intervention, de 2 à 12 par mois. Pour comparer les formats, un CMO senior en CDI représente 80 à 150 k€ par an en coût total employeur. Le premier échange permet de préciser votre besoin avant de cadrer une proposition.",
        },
        {
          q: "Quelle est la durée d'une mission ?",
          a: "La durée et le rythme sont convenus selon vos objectifs, votre organisation et le périmètre retenu. La mission commence par un diagnostic et une priorisation, puis se poursuit par le pilotage de la mise en œuvre. Les modalités d'évolution ou de relais vers une équipe interne sont à cadrer ensemble.",
        },
        {
          q: "Quelle différence entre un directeur marketing externalisé et un directeur marketing de transition ?",
          a: "Le directeur marketing de transition travaille à temps plein pendant une période donnée, souvent pour remplacer un départ. Le directeur marketing à temps partagé intervient quelques jours par mois, et accompagne l'entreprise dans la durée.",
        },
        {
          q: "Faites-vous aussi de la direction de la communication ?",
          a: "Oui, pour les PME qui n'ont pas de directeur de la communication : je prends en charge le positionnement, le plan de communication et la coordination des agences, en cohérence avec la stratégie marketing.",
        },
        {
          q: "Pouvez-vous faire monter en compétences mon équipe marketing actuelle ?",
          a: "Oui, c'est une partie intégrante de chaque mission et une offre à part entière : coaching du responsable marketing, ateliers, formation aux outils CRM et IA. Voir la page Montée en compétences.",
          link: { to: "/services/montee-en-competences-marketing", label: "Montée en compétences →" },
        },
        {
          q: "Directeur marketing à temps partagé ou externalisé : quelle différence ?",
          a: "Aucune : les deux expressions désignent le même rôle, une direction marketing senior qui intervient quelques jours par mois sans être salariée. « À temps partagé » décrit le rythme, « externalisé » le fait que la fonction est confiée à un prestataire extérieur. On parle aussi de Fractional CMO, le terme anglo-saxon équivalent.",
          link: { to: "/fractional-cmo", label: "Voir la page Fractional CMO →" },
        },
        {
          q: "Quand faire appel à un directeur marketing à temps partagé ?",
          a: "Quand votre marketing manque de direction : des actions et des outils existent, mais personne n'arbitre les priorités, les budgets et la mesure des résultats. C'est aussi le cas lors d'un départ de direction marketing, d'une croissance à structurer ou avant un recrutement interne à préparer. Si vous n'avez besoin que de production, une agence ou un freelance suffit.",
          link: { to: "/blog/cmo-externalise-vs-agence-vs-recrutement", label: "Lire le comparatif des options →" },
        },
        {
          q: "Combien de jours par mois faut-il prévoir pour un directeur marketing à temps partagé ?",
          a: "Trois formats existent : léger (2 à 4 jours par mois) pour le pilotage et les arbitrages, standard (4 à 6 jours) pour piloter et déployer, intensif (8 à 12 jours) pour les phases de mise en place ou de transformation. Le format se définit au cadrage et peut évoluer au fil de la mission.",
          link: { to: "/blog/fractional-cmo-definition-missions-tarifs", label: "Voir le détail des formats →" },
        },
        {
          q: "Un directeur marketing à temps partagé peut-il travailler à distance ?",
          a: "Oui. La majorité des missions se déroule à distance, avec des points réguliers et des outils collaboratifs. Les interventions en présentiel ont lieu à Bordeaux, en Gironde et en Nouvelle-Aquitaine ; ailleurs en France, des déplacements ponctuels sont possibles pour les ateliers ou les comités de direction, à convenir lors du cadrage.",
          link: { to: "/fractional-cmo-france", label: "Voir le fonctionnement partout en France →" },
        },
      ],
    },
    en: {
      h1: "Part-Time Marketing Director, Outsourced — Senior Leadership for SMEs",
      title: "Part-time & outsourced marketing director | CMO Partner",
      description:
        "A part-time marketing director (outsourced, or fractional CMO) leads your strategy, team and CRM a few days per month, without a full-time hire. 2 to 12 days per month, quoted per engagement.",
      intro:
        "Part-time marketing director, outsourced marketing director, fractional CMO: three names for the same need — senior marketing leadership that makes decisions, without a permanent hire. " +
        LOCAL_EN,
      sections: [
        {
          h2: "What French SMEs actually need",
          paragraphs: [
            "Many SMEs have one or two operational marketers, an underused CRM and a CEO with no time to arbitrate. The result is activity without prioritisation or measurement.",
            "An outsourced marketing director brings that level of arbitration: what to do, in what order, with what budget, and how to measure it.",
          ],
        },
        {
          h2: "What the engagement covers",
          paragraphs: ["A leadership scope, not an execution scope."],
          bullets: [
            "Marketing strategy and a 12-month plan tied to commercial objectives.",
            "Management of the internal team and agency partners.",
            "CRM and customer data structuring.",
            "Performance reporting for the CEO and the board.",
          ],
        },
        {
          h2: "A flexible, reversible model",
          paragraphs: [
            "Engagements usually run from 2 to 12 days per month over 6 to 12 months. They can scale down once the system runs, or lead to hiring an in-house marketing director whose scope and onboarding I help define.",
          ],
        },
      ],
      faq: [
        {
          q: "What is an outsourced marketing director?",
          a: "An experienced marketing director who takes responsibility for a company's marketing function on a part-time basis, typically 2 to 12 days per month, without being an employee. They set strategy, lead the team and partners and report results to the CEO.",
        },
        {
          q: "Part-time marketing director, outsourced marketing director, fractional CMO: is there a difference?",
          a: "No. The three terms describe the same role: a senior marketing leader who works with your company a few days per month without being on payroll. \"Part-time\" describes the rhythm, \"outsourced\" the external status, and \"fractional CMO\" is the term used in SaaS and scale-up contexts.",
        },
        {
          q: "How is it different from a freelance marketing manager?",
          a: "A freelance marketing manager usually handles execution: campaigns, content, tools. An outsourced marketing director operates at leadership level: strategy, budget, prioritisation, management and board reporting.",
        },
        {
          q: "How much does it cost?",
          a: "You only pay for the days you actually need (2 to 12 per month), with no payroll charges or long commitment, whereas a senior full-time CMO costs €80–150k per year in total employer cost. Billing is a monthly retainer based on the number of days.",
        },
        {
          q: "How long does an engagement last?",
          a: "Usually 6 to 12 months, with a first 90-day phase covering the audit, a first conversion loop and its industrialisation.",
        },
      ],
    },
  },

  "growth-architecture": {
    path: "/services/growth-architecture",
    related: ["fractional-cmo", "crm-lifecycle", "ai-marketing"],
    fr: {
      h1: "Growth Architecture — Transformer vos actifs en pipeline",
      title: "Growth Architecture | Transformer vos actifs marketing en pipeline",
      description:
        "Audit des actifs marketing, architecture de conversion, scoring et automation. Un système de croissance qui tourne sans intervention manuelle.",
      intro:
        "Contenu, audience, base clients : les actifs existent déjà. Ce qui manque, c'est l'architecture qui les relie à une mécanique de conversion mesurable. " +
        LOCAL_FR,
      sections: [
        {
          h2: "Audit des actifs existants",
          paragraphs: [
            "La première étape ne consiste jamais à produire davantage. J'analyse ce qui convertit déjà sans que vous le sachiez : pages, contenus, séquences, segments de base, points de friction.",
          ],
        },
        {
          h2: "Construction du système",
          paragraphs: ["Ensuite, on câble."],
          bullets: [
            "Architecture CRM et modèle de données client.",
            "Scoring comportemental et étapes de cycle de vie.",
            "Séquences de nurture par segment et par intention.",
            "Tableaux de bord de performance et attribution.",
          ],
        },
        {
          h2: "Industrialisation",
          paragraphs: [
            "Ce qui fonctionne passe en automation, le reste est arrêté. L'équipe est formée pour faire évoluer le système après la mission.",
          ],
        },
      ],
      faq: [
        {
          q: "Qu'est-ce que la growth architecture ?",
          a: "C'est la conception du système qui relie vos actifs marketing existants (contenu, audience, base clients) à une mécanique de conversion : segmentation, scoring, séquences, automation et mesure. L'objectif est un pipeline prévisible plutôt que des campagnes isolées.",
        },
        {
          q: "Combien de temps dure la mise en place ?",
          a: "Environ 90 jours : deux semaines d'audit, un mois pour une première boucle mesurable, puis deux mois d'industrialisation et de formation des équipes.",
        },
        {
          q: "Sur quels outils travaillez-vous ?",
          a: "Principalement HubSpot, ainsi que Salesforce et les principaux outils d'automation et d'analytics déjà en place chez le client.",
        },
      ],
    },
    en: {
      h1: "Growth Architecture — Turning your assets into pipeline",
      title: "Growth Architecture | Turn marketing assets into pipeline",
      description:
        "Marketing asset audit, conversion architecture, scoring and automation. A growth system that runs without manual work.",
      intro:
        "Content, audience, customer base: the assets already exist. What is missing is the architecture connecting them to a measurable conversion engine. " +
        LOCAL_EN,
      sections: [
        {
          h2: "Audit of existing assets",
          paragraphs: [
            "The first step is never to produce more. I analyse what already converts without you realising it: pages, content, sequences, database segments and friction points.",
          ],
        },
        {
          h2: "Building the system",
          paragraphs: ["Then we wire it up."],
          bullets: [
            "CRM architecture and customer data model.",
            "Behavioural scoring and lifecycle stages.",
            "Nurture sequences by segment and intent.",
            "Performance dashboards and attribution.",
          ],
        },
        {
          h2: "Industrialisation",
          paragraphs: [
            "What works gets automated, the rest gets stopped. The team is trained to evolve the system after the engagement.",
          ],
        },
      ],
      faq: [
        {
          q: "What is growth architecture?",
          a: "It is the design of the system connecting your existing marketing assets (content, audience, customer base) to a conversion engine: segmentation, scoring, sequences, automation and measurement. The goal is predictable pipeline rather than isolated campaigns.",
        },
        {
          q: "How long does implementation take?",
          a: "Around 90 days: two weeks of audit, one month for a first measurable loop, then two months of industrialisation and team training.",
        },
        {
          q: "Which tools do you use?",
          a: "Mainly HubSpot, plus Salesforce and the automation and analytics tools already in place at the client.",
        },
      ],
    },
  },

  "crm-lifecycle": {
    path: "/services/crm-lifecycle",
    related: ["fractional-cmo", "growth-architecture", "ai-marketing"],
    fr: {
      h1: "CRM & Lifecycle Marketing — HubSpot, Salesforce, Automation",
      title: "CRM & Lifecycle Marketing | HubSpot, Salesforce, automation",
      description:
        "Déploiement et restructuration HubSpot ou Salesforce : workflows, lead scoring, nurture, automation et formation des équipes.",
      intro:
        "Un CRM mal structuré coûte plus cher qu'il ne rapporte. Je déploie ou reconstruis l'architecture pour que le marketing et le commerce travaillent sur la même donnée. " +
        LOCAL_FR,
      sections: [
        {
          h2: "Déploiement ou restructuration",
          paragraphs: [
            "Propriétés, étapes de cycle de vie, pipelines, workflows, droits et intégrations : je repars du modèle de données et de la réalité du processus commercial, pas des fonctionnalités de l'outil.",
          ],
        },
        {
          h2: "Lifecycle et automation",
          paragraphs: ["Le lifecycle transforme une base de contacts dormante en source de revenus."],
          bullets: [
            "Lead scoring et définition partagée du lead qualifié.",
            "Séquences d'onboarding, de nurture et de réactivation.",
            "Alertes commerciales et SLA de traitement.",
            "Reporting de conversion étape par étape.",
          ],
        },
        {
          h2: "Formation des équipes",
          paragraphs: [
            "Aucun système ne survit sans adoption. Marketing et sales sont formés sur leurs usages réels, avec une documentation utilisable après mon départ.",
          ],
        },
      ],
      faq: [
        {
          q: "Travaillez-vous sur HubSpot et Salesforce ?",
          a: "Oui, sur les deux : déploiement depuis zéro, restructuration d'une instance existante, migration ou intégration avec les outils métier déjà utilisés.",
        },
        {
          q: "Qu'est-ce que le lifecycle marketing ?",
          a: "C'est l'organisation des actions marketing par étape de la relation client : acquisition, activation, nurturing, conversion, fidélisation et réactivation, chaque étape ayant ses messages, ses déclencheurs et ses indicateurs.",
        },
        {
          q: "Formez-vous les équipes internes ?",
          a: "Oui. La formation des équipes marketing et commerciales fait partie de chaque mission, avec une documentation des workflows pour que le système reste maintenable.",
        },
      ],
    },
    en: {
      h1: "CRM & Lifecycle Marketing — HubSpot, Salesforce, Automation",
      title: "CRM & Lifecycle Marketing | HubSpot, Salesforce, automation",
      description:
        "HubSpot or Salesforce deployment and redesign: workflows, lead scoring, nurture, automation and team training.",
      intro:
        "A badly structured CRM costs more than it returns. I deploy or rebuild the architecture so marketing and sales work from the same data. " +
        LOCAL_EN,
      sections: [
        {
          h2: "Deployment or redesign",
          paragraphs: [
            "Properties, lifecycle stages, pipelines, workflows, permissions and integrations: I start from the data model and the real sales process, not from the tool's feature list.",
          ],
        },
        {
          h2: "Lifecycle and automation",
          paragraphs: ["Lifecycle turns a dormant contact base into a revenue source."],
          bullets: [
            "Lead scoring and a shared definition of a qualified lead.",
            "Onboarding, nurture and reactivation sequences.",
            "Sales alerts and response SLAs.",
            "Stage-by-stage conversion reporting.",
          ],
        },
        {
          h2: "Team training",
          paragraphs: [
            "No system survives without adoption. Marketing and sales are trained on their real workflows, with documentation usable after I leave.",
          ],
        },
      ],
      faq: [
        {
          q: "Do you work with HubSpot and Salesforce?",
          a: "Yes, both: deployment from scratch, redesign of an existing instance, migration, or integration with the business tools already in use.",
        },
        {
          q: "What is lifecycle marketing?",
          a: "It is organising marketing by stage of the customer relationship: acquisition, activation, nurturing, conversion, retention and reactivation, each with its own messages, triggers and metrics.",
        },
        {
          q: "Do you train internal teams?",
          a: "Yes. Training marketing and sales teams is part of every engagement, with workflow documentation so the system stays maintainable.",
        },
      ],
    },
  },

  "ai-marketing": {
    path: "/services/ai-marketing",
    related: ["fractional-cmo", "growth-architecture", "crm-lifecycle"],
    fr: {
      h1: "IA & Agentic Marketing — Automatisation et Agents IA",
      title: "IA & Agentic Marketing | Automatisation et agents IA marketing",
      description:
        "Identification des workflows automatisables et construction d'agents IA (Claude, n8n) intégrés à votre organisation marketing.",
      intro:
        "30 à 50 % du temps d'une équipe marketing part dans des tâches répétitives. L'objectif n'est pas d'ajouter des outils, mais de retirer du travail manuel. " +
        LOCAL_FR,
      sections: [
        {
          h2: "Cartographie des workflows",
          paragraphs: [
            "On commence par identifier les tâches répétitives à forte fréquence : qualification, reporting, production de contenu, enrichissement de données, suivi concurrentiel.",
          ],
        },
        {
          h2: "Construction des agents",
          paragraphs: ["Des agents utiles, intégrés aux outils existants, pas des démos."],
          bullets: [
            "Agents de qualification et d'enrichissement connectés au CRM.",
            "Automatisations n8n entre les outils marketing et commerciaux.",
            "Assistants de production et de relecture de contenu.",
            "Reporting généré automatiquement pour la direction.",
          ],
        },
        {
          h2: "Adoption et gouvernance",
          paragraphs: [
            "Cadre d'usage, contrôle qualité et formation : l'IA ne remplace pas le jugement marketing, elle libère du temps pour l'exercer.",
          ],
          link: { to: "/services/montee-en-competences-marketing", label: "Former vos équipes à l'IA : la montée en compétences →" },
        },
      ],
      faq: [
        {
          q: "Qu'est-ce que l'agentic marketing ?",
          a: "C'est l'usage d'agents IA autonomes pour exécuter des tâches marketing récurrentes — qualification de leads, enrichissement de données, production de contenu, reporting — en s'intégrant aux outils existants comme le CRM ou les plateformes d'automation.",
        },
        {
          q: "Quels gains peut-on attendre ?",
          a: "En pratique, 30 à 50 % du temps consacré aux tâches répétitives peut être récupéré, ce temps étant réinvesti dans la stratégie, la création et la relation client.",
        },
        {
          q: "Faut-il une équipe technique ?",
          a: "Non. Les agents sont construits avec des outils accessibles (Claude, n8n) et documentés pour être maintenus par l'équipe marketing elle-même.",
        },
      ],
    },
    en: {
      h1: "AI & Agentic Marketing — Automation and AI agents",
      title: "AI & Agentic Marketing | Marketing automation and AI agents",
      description:
        "Identify automatable workflows and build AI agents (Claude, n8n) embedded in your marketing organisation.",
      intro:
        "30 to 50% of a marketing team's time goes into repetitive tasks. The goal is not to add tools, but to remove manual work. " +
        LOCAL_EN,
      sections: [
        {
          h2: "Workflow mapping",
          paragraphs: [
            "We start by identifying high-frequency repetitive tasks: qualification, reporting, content production, data enrichment, competitive monitoring.",
          ],
        },
        {
          h2: "Building the agents",
          paragraphs: ["Useful agents wired into existing tools, not demos."],
          bullets: [
            "Qualification and enrichment agents connected to the CRM.",
            "n8n automations between marketing and sales tools.",
            "Content production and review assistants.",
            "Automatically generated leadership reporting.",
          ],
        },
        {
          h2: "Adoption and governance",
          paragraphs: [
            "Usage framework, quality control and training: AI does not replace marketing judgement, it frees up time to exercise it.",
          ],
        },
      ],
      faq: [
        {
          q: "What is agentic marketing?",
          a: "It is the use of autonomous AI agents to run recurring marketing tasks — lead qualification, data enrichment, content production, reporting — integrated with existing tools such as the CRM or automation platforms.",
        },
        {
          q: "What gains can be expected?",
          a: "In practice, 30 to 50% of the time spent on repetitive tasks can be recovered and reinvested in strategy, creativity and customer relationships.",
        },
        {
          q: "Do we need a technical team?",
          a: "No. Agents are built with accessible tools (Claude, n8n) and documented so the marketing team can maintain them.",
        },
      ],
    },
  },

  "directeur-digital-externalise": {
    path: "/directeur-digital-externalise",
    related: ["directeur-marketing-externalise", "crm-lifecycle", "ai-marketing"],
    fr: {
      h1: "Directrice digitale externalisée : un CDO à temps partagé pour votre PME",
      title: "Directeur digital externalisé | CDO à temps partagé pour PME — CMO Partner",
      description:
        "Directrice digitale externalisée (Fractional CDO) pour PME et ETI : CRM, data, automatisation et IA pilotés par une experte senior, 2 à 12 jours par mois, sans recrutement CDI.",
      intro:
        "Vos outils se sont empilés, vos données sont dispersées et l'IA reste un sujet de réunion. En tant que directrice digitale externalisée, je remets de la cohérence dans votre écosystème et je le fais servir la croissance. " +
        LOCAL_FR,
      sections: [
        {
          h2: "Chief Digital Officer externalisé : pour quelles entreprises ?",
          paragraphs: [
            "Des PME de 10 à 300 personnes dont les outils se sont empilés sans cohérence.",
          ],
          bullets: [
            "CRM sous-exploité.",
            "Données dispersées entre plusieurs outils.",
            "Automatisations absentes.",
            "IA non intégrée aux processus.",
          ],
        },
        {
          h2: "Ce que couvre la mission",
          paragraphs: ["Du diagnostic à l'autonomie de vos équipes."],
          bullets: [
            "Audit de l'écosystème digital : site, CRM, outils, données.",
            "Feuille de route de transformation sur 90 jours.",
            "Déploiement CRM et automatisations (voir CRM & Lifecycle Marketing).",
            "Intégration de l'IA dans les processus marketing (voir IA & Agentic Marketing).",
            "Choix et coordination des prestataires techniques.",
            "Formation des équipes (voir Montée en compétences).",
          ],
          link: { to: "/services/crm-lifecycle", label: "CRM & Lifecycle Marketing →" },
        },
        {
          h2: "CMO ou CDO externalisé : quelle différence ?",
          paragraphs: [
            "Le CMO porte la croissance et l'acquisition ; le CDO porte les outils, la data et la transformation. Dans une PME, les deux rôles se recouvrent largement, et c'est souvent la même personne.",
          ],
          link: { to: "/services/ai-marketing", label: "IA & Agentic Marketing →" },
        },
        {
          h2: "Formats",
          paragraphs: ["Trois formats, ajustables au fil de la mission."],
          table: {
            head: ["Format", "Jours par mois", "Pour quoi"],
            rows: [
              ["Léger", "2 à 4 jours", "Pilotage et arbitrage"],
              ["Standard", "4 à 6 jours", "Pilotage et déploiement"],
              ["Intensif", "8 à 12 jours", "Phases de déploiement ou de transformation"],
            ],
          },
          after: ["Tarif sur devis, selon le format et la durée."],
          link: { to: "/services/montee-en-competences-marketing", label: "Montée en compétences →" },
        },
        {
          h2: "Résultats",
          paragraphs: ["Deux exemples de missions CRM, automatisation et expansion."],
          bullets: [
            "smsmode : +50 % de leads inbound, +25 % de ROI marketing, plus de 10 M€ de MRR supportés avec une équipe de plus de 10 personnes.",
            "Lumivero : +30 % de croissance du chiffre d'affaires, 1 M€ de nouveaux marchés ouverts.",
          ],
        },
      ],
      faq: [
        { q: "Quelle différence entre un directeur digital externalisé et un DSI externalisé ?", a: "Le DSI gère l'infrastructure et la sécurité informatique. Le directeur digital porte les usages business : CRM, marketing automation, data client, IA appliquée à la vente et au marketing." },
        { q: "Faut-il un CDO quand on est une PME ?", a: "Rarement à temps plein. Un CDO à temps partagé permet de structurer les outils et les données sans le coût d'un poste de direction permanent." },
        { q: "Combien de jours par mois ?", a: "De 2 à 12 jours par mois selon le format ; les phases de déploiement se font généralement en format intensif." },
        { q: "Intervenez-vous à Bordeaux ?", a: "Oui, en présentiel à Bordeaux, en Gironde et en Nouvelle-Aquitaine, et à distance dans toute la France." },
      ],
    },
  },
  "montee-en-competences-marketing": {
    path: "/services/montee-en-competences-marketing",
    related: ["crm-lifecycle", "ai-marketing", "directeur-marketing-externalise"],
    fr: {
      h1: "Faire monter en compétences votre équipe marketing actuelle",
      title: "Montée en compétences de votre équipe marketing | Coaching et formation — CMO Partner",
      description:
        "Coaching du responsable marketing, ateliers et formation aux outils CRM et IA : faites monter en compétences votre équipe marketing actuelle avec une Fractional CMO senior.",
      intro:
        "Vous avez déjà une équipe marketing, mais elle manque de méthode, d'outils ou de recul stratégique. Plutôt que de recruter ou d'externaliser, je la fais progresser sur le terrain, sur vos vrais sujets.",
      sections: [
        {
          h2: "Pour qui",
          paragraphs: ["Pour les entreprises qui veulent renforcer l'équipe en place plutôt que la remplacer."],
          bullets: [
            "Responsable marketing seul ou junior qui a besoin d'un mentor senior.",
            "Équipe qui exécute sans méthode de priorisation ni de mesure.",
            "Entreprise qui déploie un CRM ou des outils IA et veut que l'équipe les maîtrise.",
            "Dirigeant qui prépare la fin d'un accompagnement externe.",
          ],
        },
        {
          h2: "Les 4 formats",
          paragraphs: ["À combiner selon vos besoins."],
          bullets: [
            "Coaching du responsable marketing : séances individuelles régulières (priorisation, pilotage des KPIs, management des prestataires, posture face à la direction).",
            "Ateliers d'équipe : positionnement, personas, plan marketing, définition du lead qualifié avec les commerciaux.",
            "Formation aux outils : CRM (HubSpot, Salesforce, Brevo), marketing automation, usage de l'IA générative dans le marketing.",
            "Mentoring pendant la mission : chaque action pilotée est l'occasion de transmettre la méthode, avec documentation systématique.",
          ],
        },
        {
          h2: "Comment ça se passe",
          paragraphs: ["Un accompagnement en quatre temps."],
          bullets: [
            "Diagnostic des compétences et des besoins.",
            "Plan de progression sur 3 mois.",
            "Ateliers et coaching sur des cas réels de l'entreprise.",
            "Point d'étape et ajustement.",
          ],
        },
        {
          h2: "Pourquoi une Fractional CMO plutôt qu'un organisme de formation ?",
          paragraphs: ["Parce que la formation se fait sur votre marketing, pas à côté."],
          bullets: [
            "On forme sur vos projets réels, pas sur des cas génériques.",
            "La formation est reliée à des résultats business.",
            "Le suivi continue après les ateliers.",
          ],
        },
      ],
      faq: [
        { q: "Peut-on combiner montée en compétences et pilotage du marketing ?", a: "Oui, c'est le cas le plus fréquent : je pilote le marketing et je fais progresser l'équipe en parallèle, pour qu'elle gagne en autonomie au fil de la mission." },
        { q: "Combien de temps dure un accompagnement ?", a: "Généralement 3 mois pour un plan de progression, renouvelable. Le coaching peut aussi se faire au format léger (2 à 4 jours par mois)." },
        { q: "Les formations ont-elles lieu sur site ?", a: "Oui à Bordeaux et en Gironde ; à distance partout en France." },
        { q: "Aidez-vous à recruter un profil marketing ?", a: "Oui : définition du poste, participation aux entretiens et accompagnement de l'intégration." },
      ],
    },
  },
  "a-propos": {
    path: "/a-propos",
    related: ["fractional-cmo", "fractional-cmo-bordeaux", "growth-architecture"],
    fr: {
      h1: "Tiphany Rigaudias, Fractional CMO B2B & B2C",
      title: "Tiphany Rigaudias — Fractional CMO & directrice marketing externalisée | CMO Partner",
      description:
        "Fractional CMO franco-américaine basée à Bordeaux : 16 ans de direction marketing internationale (Decathlon, Lavazza, smsmode, MaxiCoffee) et builder de produits SaaS.",
      intro:
        "CMO qui construit, builder qui pense stratégie. Franco-américaine, bilingue, basée à Bordeaux. " + LOCAL_FR,
      sections: [
        {
          h2: "16 ans de direction marketing internationale",
          paragraphs: [
            "Decathlon pendant 10 ans à l'international, puis Lavazza, smsmode et MaxiCoffee. Membre du comité de direction dans chacun de ces rôles, avec la responsabilité de la stratégie, des équipes et de la performance.",
          ],
        },
        {
          h2: "Builder de produits SaaS",
          paragraphs: [
            "En parallèle, j'ai lancé from scratch trois SaaS — CosKitchen, PerfectCV et BatiPro — de la conception à l'acquisition, sur une stack moderne. Cette pratique du produit change la façon dont je conçois les systèmes marketing.",
          ],
        },
        {
          h2: "Pourquoi une Fractional CMO indépendante ?",
          paragraphs: [
            "Les plateformes et collectifs de CMO à temps partagé vous proposent un profil parmi d'autres. Avec CMO Partner, la personne que vous rencontrez au premier appel est celle qui travaille sur votre marketing, du diagnostic jusqu'à la transmission à vos équipes.",
          ],
          bullets: [
            "Un seul interlocuteur senior, sans intermédiaire ni commission de mise en relation.",
            "Une double compétence rare : direction marketing en COMEX et construction concrète des outils (CRM, automatisations, agents IA).",
            "Une expérience B2B et B2C, sur des marchés internationaux.",
            "Un réseau de freelances de confiance mobilisable si votre projet demande des bras supplémentaires, piloté par la même direction.",
          ],
        },
        {
          h2: "Une double culture rare",
          paragraphs: [
            "Je suis à l'aise en COMEX et en terminal. C'est cette double culture — CMO et builder — qui permet d'aligner vision business et exécution produit là où beaucoup choisissent l'un ou l'autre.",
          ],
          bullets: ["Franco-américaine", "Bilingue français / anglais", "Basée à Bordeaux, missions partout en France"],
        },
      ],
      faq: [
        {
          q: "Quel est votre parcours ?",
          a: "16 ans de direction marketing internationale chez Decathlon (10 ans), Lavazza, smsmode et MaxiCoffee, avec un rôle de membre du comité de direction à chaque poste, complétés par le lancement de trois produits SaaS en propre.",
        },
        {
          q: "Travaillez-vous seule ou avec une équipe ?",
          a: "Je dirige chaque mission personnellement. Quand un projet demande de l'exécution spécialisée (SEA, design, développement), je mobilise des freelances de confiance que je pilote directement.",
        },
        {
          q: "Dans quelles langues travaillez-vous ?",
          a: "En français et en anglais. Franco-américaine, j'ai piloté des équipes et des marchés en France, au Royaume-Uni, en Italie, en Espagne, en Allemagne, aux États-Unis et en Colombie.",
        },
        {
          q: "Où êtes-vous basée ?",
          a: "À Bordeaux, avec des missions en présentiel en Gironde et en Nouvelle-Aquitaine, et à distance partout en France et à l'international.",
        },
      ],
    },
    en: {
      h1: "CMOPartner — CMO & Product Builder",
      title: "About CMO Partner — Fractional CMO in Bordeaux, France",
      description:
        "About CMO Partner: 16 years of international marketing leadership (Decathlon, Lavazza, smsmode, MaxiCoffee). Part-time marketing leadership from Bordeaux, across France.",
      intro: "A CMO who builds, a builder who thinks strategically. French-American, bilingual, based in Bordeaux. " + LOCAL_EN,
      sections: [
        {
          h2: "16 years of international marketing leadership",
          paragraphs: [
            "Ten years at Decathlon internationally, then Lavazza, smsmode and MaxiCoffee. An executive committee member in each role, accountable for strategy, teams and performance.",
          ],
        },
        {
          h2: "SaaS product builder",
          paragraphs: [
            "In parallel I launched three SaaS products from scratch — CosKitchen, PerfectCV and BatiPro — from concept to acquisition on a modern stack. That product practice changes how I design marketing systems.",
          ],
        },
        {
          h2: "A rare dual culture",
          paragraphs: [
            "I am equally comfortable in the boardroom and the terminal. This dual CMO-builder culture aligns business vision and product execution where many choose one or the other.",
          ],
          bullets: ["French-American", "Bilingual French / English", "Based in Bordeaux, engagements across France"],
        },
      ],
      faq: [
        {
          q: "What is CMOPartner' background?",
          a: "16 years of international marketing leadership at Decathlon (10 years), Lavazza, smsmode and MaxiCoffee as an executive committee member, alongside launching three SaaS products of her own.",
        },
        {
          q: "Which languages do you work in?",
          a: "French and English. As a French-American, I have led international teams and markets throughout my career.",
        },
        {
          q: "Where are you based?",
          a: "In Bordeaux, working on site across Nouvelle-Aquitaine and remotely throughout France and internationally.",
        },
      ],
    },
  },
};

export const pageKeys = Object.keys(landingPages);
